import { useEffect, useRef } from 'react';

interface AutoScrollManagerProps {
  isActive: boolean;
  showQuickActions: boolean;
  onAutoScrollStart: () => void;
  onAutoScrollStop: () => void;
}

const AutoScrollManager: React.FC<AutoScrollManagerProps> = ({
  isActive,
  showQuickActions,
  onAutoScrollStart,
  onAutoScrollStop
}) => {
  const autoScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to handle auto-scroll - disable when QuickActions are visible
  useEffect(() => {
    if (isActive && !showQuickActions) {
      const scrollInterval = setInterval(() => {
        if (autoScrollingRef.current) {
          const scrollPosition = window.scrollY;
          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          
          if (scrollPosition < totalHeight) {
            window.scrollBy({
              top: 1,
              behavior: 'auto'
            });
          } else {
            clearInterval(scrollInterval);
            autoScrollingRef.current = false;
            onAutoScrollStop();
          }
        }
      }, 25); // Increased frequency for smoother scrolling

      // Handle user scroll
      const handleUserScroll = () => {
        if (autoScrollingRef.current) {
          autoScrollingRef.current = false;
          onAutoScrollStop();
          clearInterval(scrollInterval);

          // Clear existing timeout
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }

          // Set a new timeout to resume auto-scroll after 3 seconds of no scrolling
          scrollTimeoutRef.current = setTimeout(() => {
            autoScrollingRef.current = true;
            onAutoScrollStart();
          }, 3000);
        }
      };

      window.addEventListener('wheel', handleUserScroll, { passive: true });
      window.addEventListener('touchmove', handleUserScroll, { passive: true });
      window.addEventListener('keydown', handleUserScroll, { passive: true });

      return () => {
        clearInterval(scrollInterval);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        window.removeEventListener('wheel', handleUserScroll);
        window.removeEventListener('touchmove', handleUserScroll);
        window.removeEventListener('keydown', handleUserScroll);
      };
    }
  }, [isActive, showQuickActions, onAutoScrollStart, onAutoScrollStop]);

  // Stop auto-scroll when QuickActions appear to allow manual scrolling
  useEffect(() => {
    if (showQuickActions && isActive) {
      autoScrollingRef.current = false;
      onAutoScrollStop();
    }
  }, [showQuickActions, isActive, onAutoScrollStop]);

  // Start auto-scroll when activated
  useEffect(() => {
    if (isActive && !showQuickActions) {
      autoScrollingRef.current = true;
      onAutoScrollStart();
    }
  }, [isActive, showQuickActions, onAutoScrollStart]);

  // This component doesn't render anything
  return null;
};

export default AutoScrollManager;
